/**
 * @license
 * Copyright 2023 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Type-only, with every value this file reads named below (AGL-2486's rule,
// applied to the render path's hottest file): a VALUE namespace import of the
// core barrel is opaque to a bundler — it cannot know which exports are read,
// so every module the barrel reaches is pinned into the published page's first
// load.
import type * as Aglyn from '@aglyn/aglyn'
import { components } from '@aglyn/aglyn/aglyn'
import { sanitizeAuthorSx } from '@aglyn/aglyn/app-utils/author-css'
import { pinRampedTypographySx } from '@aglyn/aglyn/app-utils/responsive-typography-sx'
import {
  NODE_HIDE_IF_PROP,
  NODE_HIDE_UNLESS_PROP,
} from '@aglyn/aglyn/app-utils/reusable-component-keys'
import {
  NODE_ANIMATION_DELAY_PROP,
  NODE_ANIMATION_DURATION_PROP,
  NODE_ANIMATION_EASE_PROP,
  NODE_ANIMATION_PROP,
  NODE_ANIMATION_REPEAT_PROP,
  NODE_ANIMATION_STAGGER_PROP,
  NODE_ANIMATION_STAGGER_STEP_PROP,
  NODE_ANIMATION_TRIGGER_PROP,
  resolveElementAnimation,
} from '@aglyn/aglyn/app-utils/element-animation'
import {
  EnabledPluginsContext,
  isSwitchedOffForRenderedSite,
} from '@aglyn/aglyn/app-utils/enabled-plugins-context'
import { NodeIdentityContext } from '@aglyn/aglyn/app-utils/node-identity'
import {
  NODE_COLOR_SCHEME_PROP,
  resolveElementColorScheme,
} from '@aglyn/aglyn/app-utils/element-color-scheme'
import { FEATURE_FLAG } from '@aglyn/aglyn/foundation/constants/shared'
// Deep import, NOT the barrel: `@aglyn/shared-ui-jsx`'s index re-exports the
// Pages Router hooks (`next/router`), the inline SVG icon set and the whole
// ~12,000-module MDI catalog, none of which a rendered node needs. This file
// is on every published page, so the barrel's reach is that page's reach.
import { AglynText } from '@aglyn/shared-ui-jsx/components/aglyn-text'
import {
  styled,
  ThemeProvider,
  useSiteSchemeThemes,
  useTheme,
} from '@aglyn/shared-ui-theme'
import { mergeSxProps } from '@aglyn/shared-ui-theme'
import { observer } from 'mobx-react-lite'
import { isNamespacedComponentId } from '@aglyn/aglyn/plugin-manager/plugin-contributions'
import { RealmElementsContext } from '@aglyn/aglyn/app-utils/realm-elements-context'
import {
  forwardRef,
  type HTMLAttributes,
  Suspense,
  use,
  useContext,
} from 'react'
import { isValidElementType } from 'react-is'
import RendererComponents from '../contexts/renderer-components'
import { LeafSxTransformContext } from '../contexts/leaf-sx-transform'
import { RealmBoundaryContext } from '../contexts/realm-boundary'
import { resolvePaletteVarsSx, resolveSchemeSx } from '../utils/scheme-sx'

const DefaultComponent = styled('div')({})

export interface LeafProps extends HTMLAttributes<any> {
  children?: any
  node: Aglyn.NodeSchema
  sx?: JSX.SxProps
}

export const Leaf = observer(
  forwardRef<any, LeafProps>((props, ref) => {
    const { children, node, sx, className, style, ...rest } = props

    // Pull sx/className/style out of the node's props so the spreads below
    // can never clobber the merged values composed explicitly afterwards
    // (AGL-569: `props.sx` used to overwrite the node-level sx entirely).
    //
    // The visibility directives (AGL-1314) come out here too. The compose
    // step consumes and strips them, so a rendered page never carries one —
    // but the component EDITOR renders definition nodes ungrafted, on
    // purpose (the author has to see and select the part a page will hide),
    // and there the raw `hideIf` would spread onto the element as an
    // unknown `hideif` DOM attribute.
    const {
      sx: propsSx,
      className: propsClassName,
      style: propsStyle,
      [NODE_HIDE_IF_PROP]: _hideIf,
      [NODE_HIDE_UNLESS_PROP]: _hideUnless,
      // Element animation (AGL-2486): the reserved animation props come
      // out here for the same reason the visibility directives above do —
      // they are directives to the RENDERER, and spreading them onto the
      // element would land `aglynanimation="slide-up"` as an unknown DOM
      // attribute. Destructured rather than `delete`d after the fact so the
      // `none` case is stripped too (it resolves to no animation, but it is
      // still a prop an author saved) and so the props object keeps one
      // shape.
      [NODE_ANIMATION_PROP]: _animation,
      [NODE_ANIMATION_TRIGGER_PROP]: _animationTrigger,
      [NODE_ANIMATION_DURATION_PROP]: _animationDuration,
      [NODE_ANIMATION_DELAY_PROP]: _animationDelay,
      [NODE_ANIMATION_REPEAT_PROP]: _animationRepeat,
      [NODE_ANIMATION_EASE_PROP]: _animationEase,
      [NODE_ANIMATION_STAGGER_PROP]: _animationStagger,
      [NODE_ANIMATION_STAGGER_STEP_PROP]: _animationStaggerStep,
      // A pinned color scheme (AGL-3284) is a directive to the renderer, like
      // the two groups above: consumed below, never handed to the component.
      [NODE_COLOR_SCHEME_PROP]: _colorScheme,
      ...resolvedProps
    } = (node?.resolvedProps ?? node?.props ?? {}) as Record<string, any>

    // Resolves to a class, a `data-` attribute and two custom properties; the
    // keyframes themselves live in the tenant's stylesheet, so this costs no
    // JS on any surface. The besigner canvas never ships that stylesheet
    // (same posture as the AGL-562 hidden class), so an author sees the
    // element in its normal, selectable, un-animated state while editing.
    const animation = resolveElementAnimation({
      [NODE_ANIMATION_PROP]: _animation,
      [NODE_ANIMATION_TRIGGER_PROP]: _animationTrigger,
      [NODE_ANIMATION_DURATION_PROP]: _animationDuration,
      [NODE_ANIMATION_DELAY_PROP]: _animationDelay,
      [NODE_ANIMATION_REPEAT_PROP]: _animationRepeat,
      [NODE_ANIMATION_EASE_PROP]: _animationEase,
      [NODE_ANIMATION_STAGGER_PROP]: _animationStagger,
      [NODE_ANIMATION_STAGGER_STEP_PROP]: _animationStaggerStep,
    })
    // A component whose first-party plugin does not run on the rendered site
    // is drawn exactly as an unregistered one is (AGL-3033): the registry is
    // process-global and outlives the site that loaded the bundle, and the
    // site's own browser never loads it. Its schema goes with it, so the
    // void-element and positional flags cannot tell the server a different
    // story from the client either.
    const enabledPlugins = useContext(EnabledPluginsContext)
    const registeredSchema = components.getSchema(node?.componentId)
    const offForSite = isSwitchedOffForRenderedSite(
      registeredSchema?.pluginId,
      enabledPlugins,
    )
    const Factory = offForSite ? undefined : components.getFactory(node?.componentId)
    // A signed marketplace plugin's element (AGL-3390) renders on the server,
    // and the browser registers its component a moment later. Inside the
    // element's own boundary (below) it waits for that load rather than
    // hydrating the server's HTML as an unregistered element, which would
    // not match it.
    const realmElement = isNamespacedComponentId(node?.componentId)
    const realmBoundary = useContext(RealmBoundaryContext)
    const realmLoadFor = useContext(RealmElementsContext)
    if (realmElement && realmBoundary === node?.$id && !Factory && !offForSite) {
      const loading = realmLoadFor?.(node.componentId)
      if (loading) use(loading)
    }
    const Component = isValidElementType(Factory) ? Factory : DefaultComponent

    // Self-closing components (AGL-579): a component whose schema flags it
    // selfClosing renders a void DOM element (e.g. Image -> <img>), and React
    // throws during SSR when ANY children value reaches one — even the empty
    // `[undefined, false]` the JSX below always produces. Rendering the whole
    // page 500s off one image node (blog covers, AGL-579), so honor the flag
    // here: no JSX children, and strip a stray `children` prop too.
    const schema = offForSite ? undefined : registeredSchema
    const selfClosing = Boolean(
      (schema?.flags?.selfClosing ?? 0) & FEATURE_FLAG.ENABLED,
    )
    if (selfClosing) delete resolvedProps['children']

    const textContent = selfClosing ? null : resolvedProps?.['children']

    const mergedClassName =
      [propsClassName, node?.className, className, animation?.className]
        .filter(Boolean)
        .join(' ') || undefined
    // The animation's custom properties go in FIRST so an author who typed
    // `--aglyn-anim-duration` into the Custom CSS tab still wins.
    const mergedStyle =
      animation || propsStyle || style
        ? { ...animation?.style, ...propsStyle, ...style }
        : undefined

    // Canvas-only hook (AGL-581): the besigner provides a transform that
    // re-targets viewport media queries at the artboard device width.
    // Undefined everywhere else, keeping the tenant path unchanged.
    const transformSx = useContext(LeafSxTransformContext)
    // Scheme-scoped styles (AGL-588): sites swap a single-mode theme for
    // light/dark (tenant HostThemeProvider, canvas useAglynSiteTheme), so
    // '@scheme dark' sx slices resolve here against the ACTIVE theme's
    // palette mode — the one signal that is correct in both places.
    const theme = useTheme() as {
      palette?: { mode?: string } & Record<string, unknown>
    } | null
    const activeScheme = theme?.palette?.mode === 'dark' ? 'dark' : 'light'
    // "Always light" / "Always dark" (AGL-3284). When the pinned scheme is not
    // the active one, the element re-renders itself under the site's theme
    // for that scheme, so its OWN sx — not only its descendants' — resolves
    // palette tokens and `@scheme dark` slices for the pinned scheme. The
    // inner render then sees the pinned scheme as active and takes the plain
    // path; the mode check below is what guarantees that, because a site with
    // one theme for both schemes (a single `fallback`) hands back a theme
    // whose mode never changes, and re-wrapping it would never terminate.
    // No provider (an email render, a bare test) means nothing to force.
    // Every hook above runs on both paths, so the early return keeps the
    // hook order stable when an author flips the setting.
    const pinnedScheme = resolveElementColorScheme(_colorScheme)
    const schemeThemes = useSiteSchemeThemes()
    const forcedTheme =
      pinnedScheme && pinnedScheme !== activeScheme && schemeThemes
        ? schemeThemes(pinnedScheme)
        : undefined
    // Each realm element hydrates in a boundary of its own (AGL-3390), so one
    // waiting for its bundle holds only itself. Rendered on the server too:
    // the browser's tree has to have the same boundary to hydrate into.
    if (realmElement && realmBoundary !== node?.$id) {
      return (
        <Suspense fallback={null}>
          <RealmBoundaryContext.Provider value={node?.$id}>
            <Leaf {...props} ref={ref} />
          </RealmBoundaryContext.Provider>
        </Suspense>
      )
    }
    if (forcedTheme && forcedTheme.palette?.mode === pinnedScheme) {
      return (
        <ThemeProvider theme={forcedTheme}>
          <Leaf {...props} ref={ref} />
        </ThemeProvider>
      )
    }
    // MUI array composition: later entries win on key conflicts, so the
    // node-level sx (Styles panel output) overrides props.sx.
    //
    // Palette token references (AGL-1331): the Background Fill field
    // persists a token-bound gradient stop as
    // `var(--mui-palette-primary-main, #00B0FF)`, because `backgroundImage`
    // is NOT one of the sx keys MUI resolves against the palette. They are
    // substituted here, against the site theme, so the canvas cannot pick
    // up the console's `--mui-palette-*` and the tenant (whose theme has no
    // CSS variables at all) still tracks palette changes. Values carrying
    // no reference come back by identity.
    // Author CSS (AGL-1725): `node.sx` is the Styles panel's output, and it
    // is free text — `backgroundImage` is a first-class field and the Custom
    // CSS tab is free-solo, so any property and any value can be typed. On a
    // published site that lands in the visitor's document as a real `url()`,
    // which no input validator and no lint rule can see. Scrubbed HERE, at
    // the last point before the merge, so it also catches values already
    // stored; `sx` and `props.sx` are OUR components' own styles and are
    // deliberately left alone. The scrub is scheme-only and returns its
    // input by identity when nothing is refused, so the overwhelmingly
    // common case (no `url()` at all) costs one walk and no new object.
    // Responsive type ramp: LAST of the three sx passes, and it has to be —
    // it moves a scalar `fontSize` into the `xs` slice so the author's
    // explicit size sorts after the variant's `responsiveFontSizes` at-rules,
    // and the scheme pass above is what collapses a dark-scheme slice down
    // into that same top level. Running it earlier would leave a dark
    // override's font size unpinned.
    //
    // A pinned element paints its scheme's page background and text color
    // (AGL-3284), UNDER every other style so an author's own background wins.
    // Without it an "Always dark" band with no background of its own is
    // light text on the light page behind it, and text that sets no color
    // inherits the page's dark body color. `color-scheme` makes the browser's
    // own controls and scrollbars inside the band match. Only a pinned
    // element carries this, so an unpinned one renders exactly as before —
    // and so does a pinned one on a surface with no site themes to pin to.
    const authorSx = sanitizeAuthorSx(node?.sx)
    const composedSx = pinRampedTypographySx(
      resolvePaletteVarsSx(
        resolveSchemeSx(
          pinnedScheme && schemeThemes
            ? mergeSxProps(
                {
                  colorScheme: pinnedScheme,
                  color: 'text.primary',
                  bgcolor: 'background.default',
                } as any,
                sx as any,
                propsSx as any,
                authorSx as any,
              )
            : mergeSxProps(sx as any, propsSx as any, authorSx as any),
          activeScheme,
        ),
        theme?.palette,
      ),
    )
    /**
     * Author visibility — the eye on the hierarchy row.
     *
     * LAST, and deliberately so. It has to override the element's own
     * display, not replace it: an author who hides a Stack laid out with
     * `display: flex` gets that flex back the moment they show it again,
     * because the stored style was never touched. Writing `display: none`
     * into `node.sx` would have destroyed the value it overwrote, and there
     * would be nothing left to restore.
     *
     * The same rule on both surfaces. A hidden element is absent from the
     * published page and from the canvas — "hidden" that still renders in the
     * editor is a preview of a page nobody will ever see.
     */
    const mergedSx = node?.hidden
      ? mergeSxProps(composedSx as any, { display: 'none' } as any)
      : composedSx

    // Shared leaf attributes; self-closing components must receive NO
    // children at all (AGL-579) — a separate element expression keeps the
    // childless case genuinely childless instead of `[undefined, false]`.
    const leafProps = {
      ref,
      'data-aglyn': `leaf:${node?.$id}`,
      // Before the spreads: the animation trigger/repeat attributes are ours,
      // but an element that genuinely declared one of these should win.
      ...animation?.attributes,
      ...resolvedProps,
      ...rest,
      className: mergedClassName,
      style: mergedStyle,
      sx: transformSx
        ? (transformSx(mergedSx, node?.$id) as typeof mergedSx)
        : mergedSx,
    }

    // Node identity (AGL-659): components get their node id through a
    // context rather than a prop, so a block can look up the server-seeded
    // slice of `pageData` that belongs to IT — two grids on one page have
    // different queries and must not share a seed. Nothing is added to any
    // component's props, so no unknown attribute reaches the DOM.
    // Positional children (AGL-1237): a component that splits its children
    // by index — MUI's Accordion is `[summary, ...rest]` — must receive one
    // React child per node child. The default single `<Branch>` element reads
    // as ONE child to `Children.toArray`, so the first slot swallowed the
    // whole subtree and every later slot got nothing.
    const positional = Boolean(
      (schema?.flags?.positionalChildren ?? 0) & FEATURE_FLAG.ENABLED,
    )
    const childNodes = positional ? (node?.children ?? []) : null

    const element = selfClosing ? (
      <Component {...leafProps} />
    ) : positional ? (
      <RendererComponents.Consumer>
        {({ StemComponent }) => (
          <Component {...leafProps}>
            {childNodes!.map((child: any, key: number) => (
              <StemComponent key={child?.$id ?? key} node={child} />
            ))}
            {textContent != null && (
              <AglynText>{textContent as any}</AglynText>
            )}
          </Component>
        )}
      </RendererComponents.Consumer>
    ) : (
      <Component {...leafProps}>
        {children}

        {textContent != null && (
          <AglynText>{textContent as any}</AglynText>
        )}
      </Component>
    )

    return (
      <NodeIdentityContext.Provider value={node?.$id ?? ''}>
        {element}
      </NodeIdentityContext.Provider>
    )
  }),
)
Leaf.displayName = 'Leaf'
Leaf['aglyn'] = true

export default Leaf
